import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { titleCase } from "@/lib/text";
import { NameSuggestionsPanel } from "@/components/NameSuggestionsPanel";
import {
  ListChecks, Plus, Pencil, Archive, ArchiveRestore, Loader2, Search, Tag,
  Building2, Layers, Car, Cog, GitCompareArrows, ArrowRight, AlertTriangle, Lock,
  GitMerge, Undo2, History,
} from "lucide-react";

// ---- list registry: unifies the two backends (managed-lists strings + taxonomy ids) ----
const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");

const TABS = [
  { id: "names", label: "Name", icon: Tag },
  { id: "brands", label: "Brand", icon: Building2 },
  { id: "categories", label: "Category", icon: Layers },
  { id: "part-types", label: "Part Type", icon: Layers },
  { id: "vehicle", label: "Vehicle", icon: Car },
];
const VEH_SUBS = [
  { id: "vehicle_years", label: "Years", read: "vehicle/years", icon: Car },
  { id: "vehicle_makes", label: "Makes", read: "vehicle/makes", icon: Car },
  { id: "vehicle_models", label: "Models", read: "vehicle/models", icon: Car, parent: "make" },
  { id: "vehicle_engines", label: "Engines", read: "vehicle/engines", icon: Cog, parent: "model" },
];

export default function ManagedListsPage() {
  const { has } = useAuth();
  const canManage = has("manage_lists");
  const canMerge = has("merge_lists");
  const MERGEABLE = ["names", "brands", "categories", "part-types", "vehicle_years", "vehicle_makes", "vehicle_models", "vehicle_engines"];
  const [tab, setTab] = useState("names");
  const [vehSub, setVehSub] = useState("vehicle_years");
  const listId = tab === "vehicle" ? vehSub : tab;

  const isTaxo = listId === "categories" || listId === "part-types";
  const isManaged = !isTaxo;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [dups, setDups] = useState([]);
  const [nmc, setNmc] = useState([]);

  // parent context for cascade lists
  const [makes, setMakes] = useState([]);
  const [modelsForMake, setModelsForMake] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selMake, setSelMake] = useState("");
  const [selModel, setSelModel] = useState("");
  const [selCat, setSelCat] = useState("");

  const nearMissField = { names: "name", brands: "brand", categories: "category", "part-types": "part_type" }[listId];

  // -------- data loaders --------
  const loadParents = useCallback(async () => {
    try {
      const [mk, tx] = await Promise.all([
        api.get("/managed-lists/vehicle/makes"),
        api.get("/taxonomy"),
      ]);
      setMakes(mk.data.items || []);
      setCategories(tx.data.categories || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadParents(); }, [loadParents]);

  useEffect(() => {
    if (!selMake) { setModelsForMake([]); return; }
    api.get(`/managed-lists/vehicle/models?make_id=${selMake}`).then(({ data }) => setModelsForMake(data.items || [])).catch(() => {});
  }, [selMake]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let rows = [];
      if (isTaxo) {
        const { data } = await api.get(`/taxonomy?include_archived=${showArchived}`);
        rows = (listId === "categories" ? data.categories : data.part_types).map((r) => ({ ...r }));
        if (listId === "part-types" && selCat) rows = rows.filter((r) => r.category_id === selCat);
      } else {
        const sub = VEH_SUBS.find((v) => v.id === listId);
        let url;
        if (sub) {
          url = `/managed-lists/${sub.read}?include_archived=${showArchived}`;
          if (sub.parent === "make") url = selMake ? `/managed-lists/vehicle/models?make_id=${selMake}&include_archived=${showArchived}` : null;
          if (sub.parent === "model") url = selModel ? `/managed-lists/vehicle/engines?model_id=${selModel}&include_archived=${showArchived}` : null;
        } else {
          url = `/managed-lists/${listId}?include_archived=${showArchived}`;
        }
        rows = url ? ((await api.get(url)).data.items || []) : [];
      }
      setItems(rows);
      // duplicates + near-miss (best effort)
      try {
        if (isTaxo) {
          const { data } = await api.get("/taxonomy/audit");
          setDups((listId === "categories" ? data.categories : data.part_types)?.pairs || []);
        } else {
          const { data } = await api.get(`/managed-lists/${listId}/duplicates`);
          setDups(data.pairs || []);
        }
      } catch { setDups([]); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load the list."); }
    finally { setLoading(false); }
  }, [listId, isTaxo, showArchived, selCat, selMake, selModel]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { setSearch(""); }, [listId]);

  useEffect(() => {
    if (!nearMissField) { setNmc([]); return; }
    api.get("/taxonomy/near-miss-candidates").then(({ data }) => setNmc((data.candidates || []).filter((c) => c.field === nearMissField))).catch(() => setNmc([]));
  }, [nearMissField, items]);

  const filtered = useMemo(() => {
    const q = norm(search);
    return items.filter((r) => !q || norm(r.name).includes(q));
  }, [items, search]);

  // -------- API actions --------
  const parentIdForCreate = () => {
    if (listId === "part-types") return selCat;
    if (listId === "vehicle_models") return selMake;
    if (listId === "vehicle_engines") return selModel;
    return null;
  };
  const doCreate = async (name) => {
    if (isTaxo) {
      if (listId === "categories") return (await api.post("/taxonomy/categories", { name })).data;
      return (await api.post("/taxonomy/part-types", { name, category_id: selCat, is_major: false })).data;
    }
    const parent_id = parentIdForCreate();
    return (await api.post(`/managed-lists/${listId}`, { name, parent_id })).data;
  };
  const doRename = async (id, name) => {
    if (isTaxo) return (await api.put(`/taxonomy/${listId === "categories" ? "categories" : "part-types"}/${id}`, { name })).data;
    return (await api.put(`/managed-lists/${listId}/${id}`, { name })).data;
  };
  const doArchive = async (id, archived) => {
    if (isTaxo) return (await api.post(`/taxonomy/${listId === "categories" ? "categories" : "part-types"}/${id}/archive`, { archived })).data;
    return (await api.post(`/managed-lists/${listId}/${id}/archive`, { archived })).data;
  };
  const doUsage = async (id) => {
    if (isTaxo) return (await api.get(`/taxonomy/${listId === "categories" ? "categories" : "part-types"}/${id}/usage`)).data;
    return (await api.get(`/managed-lists/${listId}/${id}/usage`)).data;
  };

  // -------- dialogs --------
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [busy, setBusy] = useState(false);
  const [rename, setRename] = useState(null); // {id,name,usage}
  const [renameVal, setRenameVal] = useState("");
  const [arch, setArch] = useState(null); // {id,name,archived,usage}
  const [merge, setMerge] = useState(null); // {loser:{id,name}, winnerId, winnerName}
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeConfirm, setMergeConfirm] = useState("");
  const [merges, setMerges] = useState([]);
  const [showMerges, setShowMerges] = useState(false);
  const canMergeList = canMerge && MERGEABLE.includes(listId);

  const parentMissing = (listId === "part-types" && !selCat) || (listId === "vehicle_models" && !selMake) || (listId === "vehicle_engines" && !selModel);
  const parentNoun = listId === "part-types" ? "category" : listId === "vehicle_models" ? "make" : "model";

  const openAdd = () => {
    if (parentMissing) { toast.error(`Pick a ${parentNoun} first.`); return; }
    setAddName(""); setAddOpen(true);
  };
  const submitAdd = async () => {
    const name = addName.trim();
    if (!name) return;
    setBusy(true);
    try { const r = await doCreate(name); toast.success(r?.created === false ? `"${name}" already exists — selected it` : `Added "${name}"`); setAddOpen(false); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openRename = async (row) => {
    setRename({ id: row.id, name: row.name, usage: null }); setRenameVal(row.name);
    try { const u = await doUsage(row.id); setRename((s) => (s && s.id === row.id ? { ...s, usage: u } : s)); } catch { /* silent */ }
  };
  const submitRename = async () => {
    const name = renameVal.trim();
    if (!name || !rename) return;
    setBusy(true);
    try { await doRename(rename.id, name); toast.success(`Renamed to "${name}"`); setRename(null); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openArchive = async (row) => {
    const goArchive = !row.archived;
    setArch({ id: row.id, name: row.name, archived: goArchive, usage: null });
    try { const u = await doUsage(row.id); setArch((s) => (s && s.id === row.id ? { ...s, usage: u } : s)); } catch { /* silent */ }
  };
  const submitArchive = async () => {
    if (!arch) return;
    setBusy(true);
    try { await doArchive(arch.id, arch.archived); toast.success(arch.archived ? `Archived "${arch.name}"` : `Restored "${arch.name}"`); setArch(null); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // -------- merge (Build 3a) --------
  const loadMerges = useCallback(() => { api.get("/managed-lists/merges?limit=25").then(({ data }) => setMerges(data.items || [])).catch(() => setMerges([])); }, []);
  useEffect(() => { if (canMerge) loadMerges(); }, [canMerge, loadMerges]);

  const openMerge = (loser, winnerId = "") => { setMerge({ loser: { id: loser.id, name: loser.name }, winnerId }); setMergePreview(null); setMergeConfirm(""); };
  useEffect(() => {
    if (!merge || !merge.winnerId) { setMergePreview(null); return; }
    api.get(`/managed-lists/${listId}/merge-preview?winner_id=${merge.winnerId}&loser_id=${merge.loser.id}`).then(({ data }) => setMergePreview(data)).catch(() => setMergePreview(null));
  }, [merge, listId]);
  const winnerOptions = useMemo(() => (merge ? items.filter((r) => !r.archived && r.id !== merge.loser.id) : []), [items, merge]);
  const winnerName = useMemo(() => winnerOptions.find((w) => w.id === merge?.winnerId)?.name || "", [winnerOptions, merge]);

  const submitMerge = async () => {
    if (!merge || !merge.winnerId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/managed-lists/${listId}/merge`, { winner_id: merge.winnerId, loser_id: merge.loser.id, confirm_name: mergeConfirm });
      toast.success(`Merged "${data.loser}" into "${data.winner}" — ${data.parts} part${data.parts === 1 ? "" : "s"} reassigned`);
      setMerge(null); await load(); loadMerges();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const undoMerge = async (m) => {
    setBusy(true);
    try { const { data } = await api.post(`/managed-lists/merges/${m.id}/undo`); toast.success(`Undone — restored ${data.restored}${data.skipped ? `, skipped ${data.skipped}` : ""}`); await load(); loadMerges(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const usageLine = (u) => {
    if (!u) return "Checking usage…";
    const bits = [];
    bits.push(`${u.parts} part${u.parts === 1 ? "" : "s"}`);
    if (u.donors) bits.push(`${u.donors} truck${u.donors === 1 ? "" : "s"}`);
    if (u.children) bits.push(`${u.children} ${u.children_label}`);
    return bits.join(" · ");
  };

  if (!canManage) {
    return (
      <div data-testid="managed-lists-denied" className="max-w-lg mt-10 mx-auto text-center">
        <Lock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">You need the <b>Manage lists</b> permission to edit the managed lists. Ask an admin to grant it.</p>
      </div>
    );
  }

  return (
    <div data-testid="managed-lists-page" className="max-w-5xl">
      <div className="flex items-center gap-2 mb-1"><ListChecks className="w-6 h-6 text-[#B01E28]" /><h1 className="font-display text-2xl font-bold tracking-tight">Managed Lists</h1></div>
      <p className="text-sm text-slate-500 mb-5">Keep the pick-lists tidy — add, rename, archive, or merge the values staff choose from. Renaming and merging update every part that uses the value; archiving hides it going forward (never deletes). Donor ties never change.</p>

      <NameSuggestionsPanel />

      {canMerge && (
        <div className="mb-4">
          <Button data-testid="ml-merges-toggle" variant="outline" onClick={() => setShowMerges((v) => !v)} className="rounded-lg h-8 text-xs"><History className="w-3.5 h-3.5 mr-1" />{showMerges ? "Hide" : "Recent merges"}{merges.length ? ` (${merges.length})` : ""}</Button>
          {showMerges && (
            <div data-testid="ml-merges-panel" className="mt-2 border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-1.5 max-w-2xl">
              {merges.length === 0 && <div className="text-xs text-slate-400">No merges yet.</div>}
              {merges.map((m) => (
                <div key={m.id} data-testid={`ml-merge-row-${m.id}`} className="flex items-center gap-2 flex-wrap text-sm bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <Badge className="rounded-full bg-slate-100 text-slate-500 border-0 text-[10px] font-mono uppercase">{m.list_key.replace(/_/g, " ")}</Badge>
                  <span className="font-medium text-[#B01E28] line-through">{m.loser_name}</span><ArrowRight className="w-3.5 h-3.5 text-slate-300" /><span className="font-medium">{m.winner_name}</span>
                  <span className="text-[11px] text-slate-400">{m.parts} part{m.parts === 1 ? "" : "s"} · {m.created_at?.replace("T", " ").slice(0, 16)} · {m.actor_email}</span>
                  {m.undone
                    ? <Badge className="ml-auto rounded-full bg-slate-200 text-slate-500 border-0 text-[10px]">undone</Badge>
                    : <Button data-testid={`ml-merge-undo-${m.id}`} size="sm" variant="outline" disabled={busy} onClick={() => undoMerge(m)} className="ml-auto rounded-lg h-7 text-xs"><Undo2 className="w-3.5 h-3.5 mr-1" />Undo</Button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* top tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4 border-b border-slate-200 pb-3">
        {TABS.map((t) => {
          const Icon = t.icon; const on = tab === t.id;
          return (
            <button key={t.id} data-testid={`ml-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-sm font-semibold transition-colors ${on ? "bg-[#B01E28] text-white" : "bg-white text-slate-600 border border-slate-200 hover:border-[#B01E28] hover:text-[#B01E28]"}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {/* vehicle sub-tabs */}
      {tab === "vehicle" && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {VEH_SUBS.map((v) => {
            const on = vehSub === v.id;
            return (
              <button key={v.id} data-testid={`ml-veh-sub-${v.id}`} onClick={() => setVehSub(v.id)}
                className={`rounded-full px-3 h-8 text-xs font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {v.label}
              </button>
            );
          })}
        </div>
      )}

      {/* parent selectors for cascade / category */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {listId === "part-types" && (
          <Select value={selCat} onValueChange={setSelCat}>
            <SelectTrigger data-testid="ml-parent-category" className="w-56 h-9 rounded-lg text-sm"><SelectValue placeholder="Filter by category…" /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id} className="text-sm">{titleCase(c.name)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {(listId === "vehicle_models" || listId === "vehicle_engines") && (
          <Select value={selMake} onValueChange={(v) => { setSelMake(v); setSelModel(""); }}>
            <SelectTrigger data-testid="ml-parent-make" className="w-48 h-9 rounded-lg text-sm"><SelectValue placeholder="Make…" /></SelectTrigger>
            <SelectContent>{makes.map((m) => <SelectItem key={m.id} value={m.id} className="text-sm">{titleCase(m.name)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {listId === "vehicle_engines" && (
          <Select value={selModel} onValueChange={setSelModel} disabled={!selMake}>
            <SelectTrigger data-testid="ml-parent-model" className="w-48 h-9 rounded-lg text-sm"><SelectValue placeholder="Model…" /></SelectTrigger>
            <SelectContent>{modelsForMake.map((m) => <SelectItem key={m.id} value={m.id} className="text-sm">{titleCase(m.name)}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input data-testid="ml-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 h-9 w-56 rounded-lg" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-500 ml-1">
          <Switch data-testid="ml-show-archived" checked={showArchived} onCheckedChange={setShowArchived} />Show archived
        </label>
        <Button data-testid="ml-add-open" onClick={openAdd} className="ml-auto rounded-lg h-9 bg-[#E0781A] hover:bg-[#C76714] text-white"><Plus className="w-4 h-4 mr-1" />Add {tab === "vehicle" ? VEH_SUBS.find((v) => v.id === listId)?.label.replace(/s$/, "").toLowerCase() : TABS.find((t) => t.id === listId)?.label.toLowerCase()}</Button>
      </div>

      {parentMissing && (
        <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-6 text-center">Pick a {parentNoun} above to view and manage its {tab === "vehicle" ? VEH_SUBS.find((v) => v.id === listId)?.label.toLowerCase() : "part types"}.</div>
      )}

      {/* list */}
      {!parentMissing && (
        loading ? (
          <div className="font-mono text-xs text-slate-400 py-10">LOADING…</div>
        ) : (
          <div className="space-y-1.5" data-testid="ml-list">
            {filtered.length === 0 && <div className="text-sm text-slate-400 py-6">No entries{search ? " match your search" : " yet"}.</div>}
            {filtered.map((row) => (
              <div key={row.id} data-testid={`ml-row-${row.id}`} className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 ${row.archived ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`}>
                <span className="font-medium text-slate-800">{titleCase(row.name)}</span>
                {listId === "part-types" && row.is_major && <Badge className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">major</Badge>}
                {row.archived && <Badge className="rounded-full bg-slate-200 text-slate-500 border-0 text-[10px]">archived</Badge>}
                <div className="ml-auto flex items-center gap-1.5">
                  {!row.archived && <Button data-testid={`ml-rename-${row.id}`} size="sm" variant="outline" onClick={() => openRename(row)} className="rounded-lg h-8 text-xs"><Pencil className="w-3.5 h-3.5 mr-1" />Rename</Button>}
                  {!row.archived && canMergeList && <Button data-testid={`ml-merge-${row.id}`} size="sm" variant="outline" onClick={() => openMerge(row)} className="rounded-lg h-8 text-xs"><GitMerge className="w-3.5 h-3.5 mr-1" />Merge</Button>}
                  {row.archived
                    ? <Button data-testid={`ml-restore-${row.id}`} size="sm" variant="outline" onClick={() => openArchive(row)} className="rounded-lg h-8 text-xs"><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Restore</Button>
                    : <Button data-testid={`ml-archive-${row.id}`} size="sm" variant="outline" onClick={() => openArchive(row)} className="rounded-lg h-8 text-xs text-slate-500 hover:text-red-600"><Archive className="w-3.5 h-3.5 mr-1" />Archive</Button>}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* duplicates / did-you-mean */}
      {(dups.length > 0 || nmc.length > 0) && (
        <div data-testid="ml-duplicates" className="mt-8 border-t border-slate-200 pt-5">
          <div className="flex items-center gap-2 mb-2"><GitCompareArrows className="w-4 h-4 text-[#B01E28]" /><h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Possible duplicates — did you mean?</h2></div>
          <p className="text-xs text-slate-500 mb-3">Flagged only — nothing is combined automatically. {canMergeList ? "Use Merge to combine a pair into one (all attached parts move to the survivor)." : "Rename one to match the other to tidy up."}</p>
          <div className="space-y-1.5">
            {dups.map((p, i) => (
              <div key={p.pair_key || i} data-testid={`ml-dup-${i}`} className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-lg p-2.5 bg-white text-sm">
                <span className="font-medium">{titleCase(p.a.name)}{typeof p.a_count === "number" && <span className="text-[11px] text-slate-400 ml-1" data-testid={`ml-dup-${i}-a-count`}>({p.a_count} part{p.a_count === 1 ? "" : "s"})</span>}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
                <span className="font-medium">{titleCase(p.b.name)}{typeof p.b_count === "number" && <span className="text-[11px] text-slate-400 ml-1" data-testid={`ml-dup-${i}-b-count`}>({p.b_count} part{p.b_count === 1 ? "" : "s"})</span>}</span>
                <Badge className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">{p.reason} · {Math.round(p.similarity * 100)}%</Badge>
                {canMergeList && p.a.id && p.b.id && <Button data-testid={`ml-dup-merge-${i}`} size="sm" variant="outline" onClick={() => openMerge({ id: p.b.id, name: p.b.name }, p.a.id)} className="ml-auto rounded-lg h-7 text-xs"><GitMerge className="w-3.5 h-3.5 mr-1" />Merge</Button>}
              </div>
            ))}
            {nmc.map((c, i) => (
              <div key={`nmc-${i}`} data-testid={`ml-nmc-${i}`} className="flex items-center gap-2 flex-wrap border border-slate-200 rounded-lg p-2.5 bg-white text-sm">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /><span className="font-semibold">{titleCase(c.canonical)}</span>
                {c.variants.map((v) => <span key={v.typed} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{v.typed}<span className="font-mono text-[10px] text-amber-500">×{v.count}</span></span>)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ADD dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) setAddOpen(false); }}>
        <DialogContent data-testid="ml-add-dialog" className="rounded-xl border-slate-200 max-w-md">
          <DialogTitle>Add a value</DialogTitle>
          <Input data-testid="ml-add-input" autoFocus value={addName} onChange={(e) => setAddName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitAdd(); }} placeholder="New value" className="rounded-lg h-11" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button data-testid="ml-add-confirm" disabled={busy || !addName.trim()} onClick={submitAdd} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* RENAME dialog with usage preview */}
      <Dialog open={!!rename} onOpenChange={(o) => { if (!o) setRename(null); }}>
        <DialogContent data-testid="ml-rename-dialog" className="rounded-xl border-slate-200 max-w-md">
          <DialogTitle>Rename "{rename?.name}"</DialogTitle>
          <div data-testid="ml-rename-usage" className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
            Renaming this will update <b>{usageLine(rename?.usage)}</b>.
          </div>
          <Input data-testid="ml-rename-input" autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }} placeholder="New name" className="rounded-lg h-11" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setRename(null)}>Cancel</Button>
            <Button data-testid="ml-rename-confirm" disabled={busy || !renameVal.trim() || renameVal.trim() === rename?.name} onClick={submitRename} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Rename & update"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ARCHIVE dialog with usage preview */}
      <Dialog open={!!arch} onOpenChange={(o) => { if (!o) setArch(null); }}>
        <DialogContent data-testid="ml-archive-dialog" className="rounded-xl border-slate-200 max-w-md">
          <DialogTitle>{arch?.archived ? "Archive" : "Restore"} "{arch?.name}"</DialogTitle>
          <div data-testid="ml-archive-usage" className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
            {arch?.archived
              ? <>This value is used by <b>{usageLine(arch?.usage)}</b>. Archiving hides it from the dropdown going forward — existing parts keep it and stay findable.{arch?.usage?.children ? <div className="mt-1 text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />It has {arch.usage.children} active {arch.usage.children_label} — archive those first.</div> : null}</>
              : <>Restore this value so it appears in the dropdown again.</>}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setArch(null)}>Cancel</Button>
            <Button data-testid="ml-archive-confirm" disabled={busy} onClick={submitArchive} className={`rounded-lg text-white ${arch?.archived ? "bg-[#B01E28] hover:bg-[#8e1820]" : "bg-emerald-600 hover:bg-emerald-700"}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (arch?.archived ? "Archive" : "Restore")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* MERGE dialog — typed confirmation + impact preview */}
      <Dialog open={!!merge} onOpenChange={(o) => { if (!o) setMerge(null); }}>
        <DialogContent data-testid="ml-merge-dialog" className="rounded-xl border-slate-200 max-w-md">
          <DialogTitle className="flex items-center gap-2"><GitMerge className="w-5 h-5 text-[#B01E28]" />Merge a value</DialogTitle>
          <p className="text-xs text-slate-500">Combine two values into one. Every part on the value you merge away is reassigned to the survivor, then the old value is archived (never deleted). This can be undone for 90 days. <b>Donor ties never change</b>{listId.startsWith("vehicle") ? " — a vehicle merge only fixes spelling; it does not re-parent any truck." : "."}</p>
          <div className="text-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Merge away (loser)</div>
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 font-medium text-[#B01E28]" data-testid="ml-merge-loser">{merge?.loser?.name}</div>
          </div>
          <div className="text-sm">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Keep (survivor)</div>
            <Select value={merge?.winnerId || ""} onValueChange={(v) => setMerge((s) => ({ ...s, winnerId: v }))}>
              <SelectTrigger data-testid="ml-merge-winner" className="h-11 rounded-lg"><SelectValue placeholder="Pick the value to keep…" /></SelectTrigger>
              <SelectContent className="max-h-72">{winnerOptions.map((w) => <SelectItem key={w.id} value={w.id} className="text-sm">{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {merge?.winnerId && (
            <div data-testid="ml-merge-preview" className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              This merges <b>{mergePreview?.loser_name || merge?.loser?.name}</b> into <b>{mergePreview?.winner_name || winnerName}</b> and reassigns <b>{mergePreview ? `${mergePreview.parts} part${mergePreview.parts === 1 ? "" : "s"}` : "…"}</b>{mergePreview?.donors ? ` (+${mergePreview.donors} trucks)` : ""}{mergePreview?.children ? ` and moves ${mergePreview.children} ${mergePreview.children_label} to the survivor` : ""}.
            </div>
          )}
          <div className="text-sm">
            <div className="text-[11px] text-slate-500 mb-1">Type <b className="text-[#B01E28]">{merge?.loser?.name}</b> to confirm</div>
            <Input data-testid="ml-merge-confirm-input" value={mergeConfirm} onChange={(e) => setMergeConfirm(e.target.value)} placeholder={merge?.loser?.name} className="rounded-lg h-11" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setMerge(null)}>Cancel</Button>
            <Button data-testid="ml-merge-confirm" disabled={busy || !merge?.winnerId || norm(mergeConfirm) !== norm(merge?.loser?.name)} onClick={submitMerge} className="rounded-lg bg-[#B01E28] hover:bg-[#8e1820] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Merge & reassign"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
